import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import Container from '../../common/components/Container/Container';
import { Routes } from '../../config/routes';
import { FlowConfigContext } from '../../providers/FlowConfigContext';
import { ValidationContext } from '../../providers/ValidationContext';
import FlowMode from '../../providers/validation/FlowMode';
import CameraChooser from './components/CameraChooser';
import { AuthenticationContext } from '../../providers/AuthenticationContext';
import StepWorker from '../../providers/validation/steps/StepWorker';
import withRouteAnalytics from '../../services/analytics/withRouteAnalytics';
import { Screens } from '../../services/analytics/screens';
import { ComplianceStoreCheckError, isComplianceStoreCheckError } from '../../services/faceAuthentication/errors';
import { GatekeeperNetworkServiceContext } from '../../providers/GatekeeperNetworkService/GatekeeperNetworkServiceContext';
import { Action } from '../../config';
import { isRateLimitError } from '../../common/errors';

export interface GenericFaceAuthScreenProps {
  route: Routes;
  description?: {
    text: string;
    link?: string;
  };
  getStepWorker: (did: string) => StepWorker;
}

function GenericFaceAuthScreen({ description, getStepWorker }: GenericFaceAuthScreenProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { networkName, action, credentialExpired } = useContext(FlowConfigContext);
  const { getRouteForNextStep, setFlowMode, processStep, showContinueOnMobile, deviceLinkContext } =
    useContext(ValidationContext);
  const [hasCameraPermission, setHasCameraPermission] = useState<boolean>(true);
  const { gknServiceData } = useContext(GatekeeperNetworkServiceContext);

  const enableBreakoutFlow =
    (gknServiceData?.constraints?.public?.proofOfPersonhood?.enableHandoverBreakoutFlow ?? false) === true;

  const [loading, setLoading] = useState<boolean>(false);
  const { getUserData } = useContext(AuthenticationContext);
  const { civicAnalyticsEvent, updateCivicAnalyticsEventData } = useContext(FlowConfigContext);

  const handleConfirm = async () => {
    setLoading(true);
    const userDataResult = await getUserData();
    if (userDataResult.isFailed()) {
      userDataResult.logError();
      setLoading(false);
      return;
    }

    const userData = userDataResult.success();
    const userDid = userData?.did;
    const result = await processStep(getStepWorker(userDid));
    console.log('result', result, result.isFailed());
    setLoading(false);
    if ((result.isFailed() || result.isCancelled() || result.isSuccessful()) && civicAnalyticsEvent) {
      const detail = result.getDetail();
      const resultEvent = { ...civicAnalyticsEvent };
      resultEvent.faceTecScanSessionId = detail?.facetecScanSessionId;
      resultEvent.faceTecScanStatus = detail?.facetecScanStatus;
      updateCivicAnalyticsEventData(resultEvent);
    }
    // Check for compliance store check error and navigate to error screen
    if (result.isFailed() && isComplianceStoreCheckError(result.failure())) {
      navigate(Routes.ERROR, {
        state: {
          errorCode: (result.failure() as ComplianceStoreCheckError)?.getComplianceStoreCheckResult()?.errorCode,
        },
      });
    }
    // Check for rate limit error and navigate to error screen
    else if (result.isFailed() && isRateLimitError(result.failure())) {
      navigate(Routes.ERROR, {
        state: {
          errorCode: 'rate_limited',
        },
      });
    }
    // If the capture selfie failed then let the user try again
    if (result.isSuccessful()) {
      const route = await getRouteForNextStep();
      if (route) navigate(route);
    }
  };

  useEffect(() => {
    // If a user browser is a chromium based browser, we can check if they have camera permission
    if (navigator.userAgent.includes('Chrome') && navigator.permissions) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      navigator.permissions.query({ name: 'camera' }).then(function (permissionStatus) {
        // Permission status can be 'granted', 'denied', or 'prompt'
        // Its a newer api though and tslint doesn't know about it
        // Webcam access is denied or in an evironment where we can't access the camera (backpack xnft), therefore we hide webcam option
        if (permissionStatus.state === 'denied') {
          setHasCameraPermission(false);
        } else {
          // permission is either `granted` or `prompt` (in which case we treat it as `granted`)
          // Prompt is the user hasn't decided to allow or deny yet so we want them to see the option
          setHasCameraPermission(true);
        }
      });
    }
  }, [navigator.permissions]);

  const handleChooseWebcam = async () => {
    // if the user can continue on mobile, change the flow to full otherwise just send him to the next step
    if (showContinueOnMobile) {
      setFlowMode(FlowMode.full);
    }
    handleConfirm();
  };

  const handleChoosePhone = () => {
    // select handover mode if the complete with phone option is selected on web
    setFlowMode(FlowMode.handover);
    navigate(Routes.CONTINUE_WITH_LINK, { state: { from: location.pathname } });
  };

  const handleChooseSystemBrowser = () => {
    // select handover mode if the "open with browser"" option is selected on web
    setFlowMode(FlowMode.handover);
    navigate(Routes.CONTINUE_WITH_LINK, { state: { from: location.pathname, enableBreakoutFlow } });
  };

  // Document refresh doesn't require a document scan, so change the title to reflect that in both normal and handoff flows
  const flowAction = credentialExpired && action === Action.REFRESH ? Action.ISSUANCE : action;
  const title =
  flowAction === Action.REFRESH || deviceLinkContext?.sourceAction === Action.REFRESH
      ? t('screens.chooseWebOrMobile.titleRefresh', { context: networkName })
      : t('screens.chooseWebOrMobile.title', { context: networkName });

  return (
    <Container>
      <CameraChooser
        title={title}
        showWebcamButton={hasCameraPermission}
        showPhoneButton={showContinueOnMobile}
        enableBreakoutFlow={enableBreakoutFlow && action !== Action.LINK}
        handleChoosePhone={handleChoosePhone}
        handleChooseWebcam={handleChooseWebcam}
        handleChooseBreakoutFlow={handleChooseSystemBrowser}
        description={description}
        loading={loading}
      />
    </Container>
  );
}
export default withRouteAnalytics(GenericFaceAuthScreen, Screens.CHOOSE_WEB_OR_MOBILE);
